import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../../contexts/DataContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import supabase from '../../../supabase';
import WorkloadIndicator from './WorkloadIndicator';

const { 
  FiSearch, FiMapPin, FiStar, FiExternalLink, FiShield, 
  FiBriefcase, FiAward, FiCheckBadge 
} = FiIcons;

const QUALIFICATION_ICONS = {
  FiShield: FiShield,
  FiCheckBadge: FiCheckBadge,
  FiStar: FiStar,
  FiAward: FiAward,
  FiBriefcase: FiBriefcase
};

const InvestigatorDirectory = () => {
  const navigate = useNavigate();
  const { utredere } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [workloads, setWorkloads] = useState({});
  const [loadingWorkload, setLoadingWorkload] = useState(true);

  useEffect(() => {
    fetchWorkloads();
  }, []);

  const fetchWorkloads = async () => {
    try {
      setLoadingWorkload(true);
      const { data: assignments, error } = await supabase
        .from('utreder_assignments')
        .select('utreder_id')
        .eq('status', 'in_progress');

      if (error) throw error;

      const counts = (assignments || []).reduce((acc, curr) => {
        acc[curr.utreder_id] = (acc[curr.utreder_id] || 0) + 1;
        return acc;
      }, {});

      setWorkloads(counts);
    } catch (err) {
      console.error('Error fetching investigator workload:', err);
    } finally {
      setLoadingWorkload(false);
    }
  };

  // Fixed mapping: use full_name from database structure
  const filtered = (Array.isArray(utredere) ? utredere : []).filter(u => 
    u.active && 
    (u.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
     u.competence_tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())))
  ).sort((a, b) => (b.subscription_plan === 'pro' ? 1 : -1));

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="bg-gradient-to-br from-indigo-900 to-slate-900 rounded-[32px] p-10 text-white shadow-xl relative overflow-hidden">
        <div className="relative z-10">
          <div className="inline-flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest mb-4 border border-white/20">
            <SafeIcon icon={FiAward} /> Verifiserte Partnere
          </div>
          <h2 className="text-4xl font-black mb-4 tracking-tight">Finn en uavhengig utreder</h2>
          <p className="text-indigo-100 max-w-xl text-lg font-medium leading-relaxed">
            Vårt nettverk av advokater, revisorer og spesialister står klare til å bistå din bedrift i krevende varslingssaker for å sikre full objektivitet.
          </p>
        </div>
        <div className="absolute -right-20 -top-20 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl"></div>
      </div>

      <div className="flex flex-col md:flex-row gap-4 items-center">
        <div className="relative flex-1 w-full">
          <SafeIcon icon={FiSearch} className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
          <input 
            type="text"
            placeholder="Søk på navn eller kompetanse (f.eks. 'Arbeidsrett', 'Gransking')..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-14 pr-6 py-5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-[20px] shadow-sm outline-none focus:ring-2 focus:ring-indigo-500 text-lg transition-all"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {filtered.map((pro) => (
          <motion.div 
            key={pro.id}
            whileHover={{ y: -6 }}
            className="bg-white dark:bg-gray-800 rounded-[32px] border border-gray-100 dark:border-gray-700 overflow-hidden shadow-sm hover:shadow-2xl transition-all group"
          >
            <div className="p-8 flex flex-col h-full">
              <div className="flex justify-between items-start mb-8">
                <div className="w-24 h-24 bg-gray-100 dark:bg-gray-700 rounded-[24px] flex items-center justify-center text-4xl font-black text-gray-400 border-4 border-white dark:border-gray-600 shadow-inner overflow-hidden group-hover:rotate-3 transition-transform">
                  {pro.branding?.logoUrl ? (
                    <img src={pro.branding.logoUrl} className="w-full h-full object-cover" />
                  ) : (
                    <span className="font-black text-4xl text-gray-400">{pro.full_name?.charAt(0)}</span>
                  )}
                </div>
                {pro.subscription_plan === 'pro' && (
                  <span className="bg-amber-100 text-amber-700 px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 border border-amber-200 animate-pulse">
                    <SafeIcon icon={FiStar} /> Pro Partner
                  </span>
                )}
              </div>

              <div className="flex-1">
                <h3 className="text-2xl font-black text-gray-900 dark:text-white mb-1 group-hover:text-indigo-600 transition-colors">
                  {pro.full_name}
                </h3>
                <p className="text-sm font-black text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                  <SafeIcon icon={FiBriefcase} /> {pro.title_role || 'Uavhengig Utreder'}
                </p>

                {/* Qualification Badges */}
                {pro.qualifications && pro.qualifications.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-6">
                    {pro.qualifications.map((q, idx) => (
                      <div 
                        key={idx}
                        title={q.label}
                        className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider border
                          ${q.color === 'blue' ? 'bg-blue-50 text-blue-700 border-blue-100' : 
                            q.color === 'indigo' ? 'bg-indigo-50 text-indigo-700 border-indigo-100' : 
                            q.color === 'amber' ? 'bg-amber-50 text-amber-700 border-amber-100' : 
                            'bg-slate-50 text-slate-700 border-slate-100'}`}
                      >
                        <SafeIcon icon={QUALIFICATION_ICONS[q.icon] || FiAward} className="text-[10px]" />
                        {q.label}
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-6 font-medium">
                  <SafeIcon icon={FiMapPin} className="text-indigo-500" /> {pro.geography || 'Hele landet'}
                </div>

                <div className="mb-6">
                  <WorkloadIndicator 
                    current={workloads[pro.id] || 0} 
                    max={pro.max_capacity || 5} 
                  />
                </div>

                <div className="flex flex-wrap gap-2 mb-8">
                  {(pro.competence_tags || []).slice(0, 4).map((tag, i) => (
                    <span key={i} className="px-3 py-1 bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg text-[11px] font-bold uppercase tracking-tight">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              <div className="pt-8 border-t border-gray-100 dark:border-gray-700 flex gap-4">
                <button 
                  onClick={() => navigate(`/u/${pro.slug}`)}
                  className="flex-1 bg-indigo-600 text-white py-4 rounded-[16px] font-black text-sm uppercase tracking-widest hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100 dark:shadow-none flex items-center justify-center gap-2"
                >
                  <SafeIcon icon={FiExternalLink} /> Se Microsite
                </button>
              </div>
            </div>
          </motion.div>
        ))}

        {filtered.length === 0 && (
          <div className="col-span-full py-24 text-center bg-white rounded-[32px] border border-dashed border-gray-300">
            <SafeIcon icon={FiSearch} size={48} className="mx-auto mb-4 text-gray-200" />
            <h4 className="text-xl font-bold text-gray-900">Ingen treff</h4>
            <p className="text-gray-500">Prøv et annet søkeord eller kontakt support for hjelp med matching.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvestigatorDirectory;